import {Component, DestroyRef, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {filter, tap} from 'rxjs';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatTabsModule} from '@angular/material/tabs';
import {MatSelectModule} from '@angular/material/select';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatDialog} from '@angular/material/dialog';
import {HeaderComponent} from '../header/header.component';
import {FooterComponent} from '../footer/footer.component';
import {CommonModule} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {environment} from '@env/environment';
import {ToastService} from '@services/toast.service';
import {ScheduleAnalysisDialogComponent} from '../schedule-analysis/schedule-analysis-dialog.component';
import {TechnicalAnalysisDialogComponent} from '../technical-analysis/technical-analysis-dialog.component';
import {ResourceType, ScheduleDetailData, ScheduleListData, ScheduleResource, ScheduleSession} from '@api/planner';
import {TableSkeletonComponent} from '../components';

@Component({
  selector: 'app-desktop',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatTabsModule,
    MatSelectModule,
    MatFormFieldModule,
    HeaderComponent,
    FooterComponent,
    TableSkeletonComponent,
  ],
  templateUrl: './desktop.component.html',
  styleUrls: ['./desktop.component.scss'],
})
export class DesktopComponent implements OnInit {
  // Helper for creating arrays in templates
  createArray(length: number): number[] {
    return Array(length).fill(0).map((_, i) => i);
  }

  selectedScheduleId: number = 0;
  selectedScheduleTabIndex: number = 0;

  get scheduleSessions(): ScheduleSession[] {
    return this.scheduleDetailData.peekItem()?.sessions ?? [];
  }

  get resourceTypes(): ResourceType[] {
    return this.scheduleDetailData.peekItem()?.resourceTypes ?? [];
  }

  days = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];
  selectedTabIndex: number = 0;

  // --- New: View mode state ---
  viewMode: 'day' | 'resource' | 'typeperday' = 'day';

  // --- Type-per-day view state ---
  selectedTypePerDayDay: number | null = null;
  selectedTypePerDayResourceTypeSlug: string | null = null;

  // --- Resource view state ---
  selectedResourceTypeSlug: string | null = null;
  selectedResourceId: number | null = null;
  resourceListForType: ScheduleResource[] = [];

  // --- LocalStorage keys ---
  private readonly STORAGE_VIEW_MODE = 'dashboardViewMode';
  private readonly STORAGE_RESOURCE_TYPE = 'dashboardResourceTypeSlug';
  private readonly STORAGE_RESOURCE_ID = 'dashboardResourceId';
  private readonly STORAGE_TYPEPERDAY_DAY = 'dashboardTypePerDayDay';
  private readonly STORAGE_TYPEPERDAY_TYPE =
    'dashboardTypePerDayResourceTypeSlug';

  // For resource view: sessions for the selected resource, grouped by day
  get resourceSessionsByDay(): { [key: number]: ScheduleSession[] } {
    if (!this.selectedResourceId || !this.selectedResourceTypeSlug) return {};
    const grouped: { [key: number]: ScheduleSession[] } = {};
    for (let i = 1; i <= 7; i++) {
      grouped[i] = [];
    }
    this.scheduleSessions.forEach((session) => {
      if (
        session.resources.some(
          (r) =>
            r.type === this.selectedResourceTypeSlug &&
            r.id === this.selectedResourceId
        )
      ) {
        if (!grouped[session.day]) grouped[session.day] = [];
        grouped[session.day].push(session);
      }
    });
    Object.keys(grouped).forEach((day) => {
      this.sortSessionsWithinDay(grouped[Number(day)]);
    });
    return grouped;
  }

  // For resource view: get all resources of a type in the current schedule
  getResourcesOfType(typeSlug: string): ScheduleResource[] {
    // Flatten all resources of this type from all sessions, dedupe by id
    const seen = new Set<number>();
    const result: ScheduleResource[] = [];
    this.scheduleSessions.forEach((session) => {
      session.resources.forEach((r) => {
        if (r.type === typeSlug && !seen.has(r.id)) {
          seen.add(r.id);
          result.push(r);
        }
      });
    });
    return result;
  }

  // For type-per-day view: get all resources of a type scheduled on a given day
  getResourcesOfTypeOnDay(typeSlug: string, day: number): ScheduleResource[] {
    const seen = new Set<number>();
    const result: ScheduleResource[] = [];
    this.scheduleSessions.forEach((session) => {
      if (session.day === day) {
        session.resources.forEach((r) => {
          if (r.type === typeSlug && !seen.has(r.id)) {
            seen.add(r.id);
            result.push(r);
          }
        });
      }
    });
    return result;
  }

  // Group sessions by day for the UI (day view)
  get sessionsByDay(): { [key: number]: ScheduleSession[] } {
    const grouped: { [key: number]: ScheduleSession[] } = {};
    for (let i = 1; i <= 7; i++) {
      grouped[i] = [];
    }
    this.scheduleSessions.forEach((session) => {
      if (!grouped[session.day]) {
        grouped[session.day] = [];
      }
      grouped[session.day].push(session);
    });
    Object.keys(grouped).forEach((day) => {
      this.sortSessionsWithinDay(grouped[Number(day)]);
    });
    return grouped;
  }

  // Get resource type slugs in the order returned by the API
  get resourceTypeSlugs(): string[] {
    return this.resourceTypes.map((rt) => rt.slug);
  }

  // Get the current version info for the selected schedule
  get selectedScheduleVersionInfo(): { number: number | null; name: string | null } {
    const schedules = this.scheduleListData.peekItems() ?? [];
    const selected = schedules.find((s) => s.id === this.selectedScheduleId);
    return {
      number: selected?.currentVersionNumber ?? null,
      name: selected?.currentVersionName ?? null,
    };
  }

  private getResourceType(slug: string): ResourceType | undefined {
    return this.resourceTypes.find((rt) => rt.slug === slug);
  }

  getResourceTypeName(slug: string): string {
    return this.getResourceType(slug)?.name ?? slug;
  }

  getResourceTypeIcon(slug: string): string | undefined {
    return this.getResourceType(slug)?.icon;
  }

  private apiBaseUrl = environment.apiBaseUrl;

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private toastService: ToastService,
    public scheduleListData: ScheduleListData,
    public scheduleDetailData: ScheduleDetailData,
    private destroyRef: DestroyRef
  ) {
  }

  ngOnInit() {
    const storedId = localStorage.getItem('selectedOrganisationId');
    if (!storedId) {
      this.toastService.warning('No organisation selected.');
      return;
    }

    this.scheduleListData.setOrganisationId(storedId);
    this.scheduleDetailData.setOrganisationId(storedId);
    this.restoreStateFromStorage();

    // Subscribe to current schedule detail for side effects
    this.scheduleDetailData.item$
      .pipe(
        filter((detail): detail is NonNullable<typeof detail> => !!detail),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.updateResourceListForType();
        const firstDayWithData = [1, 2, 3, 4, 5, 6, 7].findIndex(day => this.sessionsByDay[day]?.length > 0);
        this.selectedTabIndex = firstDayWithData >= 0 ? firstDayWithData : 0;
      });

    // Load schedules and handle initial selection
    this.scheduleListData.loadItems()
      .pipe(
        tap((schedules) => {
          if (schedules.length > 0) {
            let foundIdx = schedules.findIndex((s) => s.id === this.selectedScheduleId);
            if (foundIdx === -1) {
              this.selectedScheduleId = schedules[0].id;
              foundIdx = 0;
            }
            this.selectedScheduleTabIndex = foundIdx;
            this.loadPersistedSchedule(this.selectedScheduleId);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private restoreStateFromStorage() {
    this.selectedScheduleId = parseInt(localStorage.getItem('selectedScheduleId') ?? '0', 10);

    const viewMode = localStorage.getItem(this.STORAGE_VIEW_MODE);
    if (viewMode === 'day' || viewMode === 'resource' || viewMode === 'typeperday') {
      this.viewMode = viewMode;
    }

    this.selectedResourceTypeSlug = localStorage.getItem(this.STORAGE_RESOURCE_TYPE);
    this.selectedResourceId = parseInt(localStorage.getItem(this.STORAGE_RESOURCE_ID) ?? '', 10) || null;
    this.selectedTypePerDayDay = parseInt(localStorage.getItem(this.STORAGE_TYPEPERDAY_DAY) ?? '', 10) || null;
    this.selectedTypePerDayResourceTypeSlug = localStorage.getItem(this.STORAGE_TYPEPERDAY_TYPE);
  }

  // Fetches persisted schedule items and resource types
  loadPersistedSchedule(scheduleId: number) {
    if (!scheduleId) {
      this.toastService.warning('No schedule selected for loading details.');
      return;
    }
    this.scheduleDetailData.loadItem(scheduleId);
  }

  // Generates (or re-generates) schedule and then fetches it
  generateAndFetchSchedule(forceRegenerate: boolean) {
    if (!this.selectedScheduleId) {
      this.toastService.warning('No schedule selected for generation.');
      return;
    }
    const regenerateParam = forceRegenerate ? 1 : 0;
    this.http
      .get<any>(
        `${this.apiBaseUrl}/generate/${this.selectedScheduleId}/${regenerateParam}`
      )
      .subscribe({
        next: (data) => {
          if (data && data.success) {
            this.loadPersistedSchedule(this.selectedScheduleId);
          } else {
            this.toastService.error('Failed to generate schedule. Please try again.');
          }
        },
      });
  }

  // Change selected schedule tab and fetch new data
  onScheduleTabChange(tabIndex: number) {
    const schedules = this.scheduleListData.peekItems() ?? [];
    if (tabIndex < 0 || tabIndex >= schedules.length) return;
    this.selectedScheduleTabIndex = tabIndex;
    const selected = schedules[tabIndex];
    if (selected && selected.id !== this.selectedScheduleId) {
      this.selectedScheduleId = selected.id;
      // Store the selected schedule in localStorage
      localStorage.setItem(
        'selectedScheduleId',
        String(this.selectedScheduleId)
      );
      this.loadPersistedSchedule(this.selectedScheduleId);
    }
  }

  // --- View mode toggle ---
  setViewMode(mode: 'day' | 'resource' | 'typeperday') {
    this.viewMode = mode;
    localStorage.setItem(this.STORAGE_VIEW_MODE, mode);
    if (mode === 'resource') {
      // Default to first resource type if not set
      if (!this.selectedResourceTypeSlug && this.resourceTypes.length > 0) {
        this.selectedResourceTypeSlug = this.resourceTypes[0].slug;
      }
      this.updateResourceListForType();
    }
    if (mode === 'typeperday') {
      // Default to first day and first resource type if not set
      if (!this.selectedTypePerDayDay) {
        this.selectedTypePerDayDay = 1;
      }
      if (
        !this.selectedTypePerDayResourceTypeSlug &&
        this.resourceTypes.length > 0
      ) {
        this.selectedTypePerDayResourceTypeSlug = this.resourceTypes[0].slug;
      }
    }
  }

  // --- Resource type selection ---
  onResourceTypeChange(slug: string) {
    this.selectedResourceTypeSlug = slug;
    localStorage.setItem(this.STORAGE_RESOURCE_TYPE, slug);
    this.selectedResourceId = null;
    localStorage.removeItem(this.STORAGE_RESOURCE_ID);
    this.updateResourceListForType();
  }

  // --- Type-per-day view: day selection ---
  onTypePerDayDayChange(day: number) {
    this.selectedTypePerDayDay = day;
    localStorage.setItem(this.STORAGE_TYPEPERDAY_DAY, String(day));
  }

  // --- Type-per-day view: resource type selection ---
  onTypePerDayResourceTypeChange(slug: string) {
    this.selectedTypePerDayResourceTypeSlug = slug;
    localStorage.setItem(this.STORAGE_TYPEPERDAY_TYPE, slug);
  }

  // --- Resource selection ---
  onResourceSelect(resourceId: number) {
    this.selectedResourceId = resourceId;
    if (resourceId !== null && resourceId !== undefined) {
      localStorage.setItem(this.STORAGE_RESOURCE_ID, String(resourceId));
    } else {
      localStorage.removeItem(this.STORAGE_RESOURCE_ID);
    }
  }

  // --- Update resource list for selected type ---
  updateResourceListForType() {
    if (this.selectedResourceTypeSlug) {
      this.resourceListForType = this.getResourcesOfType(
        this.selectedResourceTypeSlug
      );
      if (this.resourceListForType.length > 0) {
        // If the previously selected resource is still available, keep it
        const found = this.resourceListForType.find(
          (r) => r.id === this.selectedResourceId
        );
        if (!found) {
          this.selectedResourceId = this.resourceListForType[0].id;
          localStorage.setItem(
            this.STORAGE_RESOURCE_ID,
            String(this.selectedResourceId)
          );
        }
      } else {
        this.selectedResourceId = null;
        localStorage.removeItem(this.STORAGE_RESOURCE_ID);
      }
    } else {
      this.resourceListForType = [];
      this.selectedResourceId = null;
      localStorage.removeItem(this.STORAGE_RESOURCE_ID);
    }
  }

  // Helper method to get resource of a specific type (by slug)
  getResourceByType(
    session: ScheduleSession,
    typeSlug: string
  ): ScheduleResource | undefined {
    return session.resources.find((resource) => resource.type === typeSlug);
  }

  // Helper: get all associated resources for a session, grouped by type
  getAssociatedResources(
    session: ScheduleSession,
    excludeType?: string | null,
    excludeId?: number
  ): { [type: string]: ScheduleResource[] } {
    const grouped: { [type: string]: ScheduleResource[] } = {};
    session.resources.forEach((r) => {
      if (
        excludeType != null &&
        excludeId != null &&
        r.type === excludeType &&
        r.id === excludeId
      )
        return;
      if (!grouped[r.type]) grouped[r.type] = [];
      grouped[r.type].push(r);
    });
    return grouped;
  }

  // Helper: check if resourceSessionsByDay is empty (for template)
  resourceSessionsEmpty(): boolean {
    return Object.values(this.resourceSessionsByDay).every(
      (arr) => arr.length === 0
    );
  }

  // Sort sessions within a day by startHour, then by first resource name
  private sortSessionsWithinDay(sessions: ScheduleSession[]): void {
    sessions.sort((a, b) => {
      if (a.startHour !== b.startHour) return a.startHour - b.startHour;
      const aFirstResource = a.resources[0]?.name ?? '';
      const bFirstResource = b.resources[0]?.name ?? '';
      return aFirstResource.localeCompare(bFirstResource);
    });
  }

  // Generate 5-minute time slots for a day
  private generateTimeSlots(sessions: ScheduleSession[]): number[] {
    if (!sessions || sessions.length === 0) return [];

    // Find min and max times
    let minTime = Math.min(...sessions.map(s => s.startHour));
    let maxTime = Math.max(...sessions.map(s => s.endHour));

    // Round down to nearest 5-minute slot
    minTime = Math.floor(minTime / 5) * 5;
    // Round up to nearest 5-minute slot
    maxTime = Math.ceil(maxTime / 5) * 5;

    // Generate slots
    const slots: number[] = [];
    for (let time = minTime; time < maxTime; time += 5) {
      slots.push(time);
    }

    return slots;
  }

  // Convert minutes to time string
  private minutesToTimeString(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  // Calculate which column a session should be in to avoid overlaps
  private calculateSessionColumns(sessions: ScheduleSession[]): Map<ScheduleSession, number> {
    const columnMap = new Map<ScheduleSession, number>();
    const sortedSessions = [...sessions].sort((a, b) =>
      a.startHour - b.startHour || a.endHour - b.endHour
    );

    // Track which time ranges are occupied in each column
    const columnOccupancy: { start: number; end: number }[][] = [];

    sortedSessions.forEach(session => {
      // Find the first available column
      let column = 0;
      let placed = false;

      while (!placed) {
        // Initialize column if it doesn't exist
        if (!columnOccupancy[column]) {
          columnOccupancy[column] = [];
        }

        // Check if this session conflicts with any session in this column
        const hasConflict = columnOccupancy[column].some(range =>
          session.startHour < range.end && range.start < session.endHour
        );

        if (!hasConflict) {
          // Place session in this column
          columnOccupancy[column].push({
            start: session.startHour,
            end: session.endHour
          });
          columnMap.set(session, column);
          placed = true;
        } else {
          // Try next column
          column++;
        }
      }
    });

    return columnMap;
  }

  private buildTimeSlotGrid(sessionsByKey: { [key: number]: ScheduleSession[] }): {
    rows: {
      timeSlot: number;
      timeString: string;
      sessionsByKeyAndColumn: { [key: number]: { [col: number]: ScheduleSession | null } }
    }[];
    maxColumns: { [key: number]: number };
  } {
    const keys = Object.keys(sessionsByKey).map(Number);
    const allSessions = keys.flatMap(k => sessionsByKey[k] || []);

    if (allSessions.length === 0) {
      return {rows: [], maxColumns: Object.fromEntries(keys.map(k => [k, 1]))};
    }

    const timeSlots = this.generateTimeSlots(allSessions);
    const columnsByKey: { [key: number]: Map<ScheduleSession, number> } = {};
    const maxColumns: { [key: number]: number } = {};

    keys.forEach(key => {
      const sessions = sessionsByKey[key] || [];
      columnsByKey[key] = this.calculateSessionColumns(sessions);
      const columnValues = Array.from(columnsByKey[key].values());
      maxColumns[key] = columnValues.length > 0 ? Math.max(...columnValues) + 1 : 1;
    });

    const rows = timeSlots.map(timeSlot => {
      const sessionsByKeyAndColumn: { [key: number]: { [col: number]: ScheduleSession | null } } = {};

      keys.forEach(key => {
        sessionsByKeyAndColumn[key] = {};
        (sessionsByKey[key] || []).forEach(session => {
          if (session.startHour <= timeSlot && session.endHour > timeSlot) {
            const column = columnsByKey[key].get(session) || 0;
            sessionsByKeyAndColumn[key][column] = session;
          }
        });
      });

      return {timeSlot, timeString: this.minutesToTimeString(timeSlot), sessionsByKeyAndColumn};
    });

    return {rows, maxColumns};
  }

  getTypePerDaySessionRows() {
    if (!this.selectedTypePerDayDay || !this.selectedTypePerDayResourceTypeSlug) return [];

    const resources = this.getResourcesOfTypeOnDay(this.selectedTypePerDayResourceTypeSlug, this.selectedTypePerDayDay);
    const sessionsByResource: { [resourceId: number]: ScheduleSession[] } = {};

    resources.forEach(r => {
      sessionsByResource[r.id] = this.scheduleSessions
        .filter(s => s.day === this.selectedTypePerDayDay && s.resources.some(res => res.id === r.id))
        .sort((a, b) => a.startHour - b.startHour || a.endHour - b.endHour);
    });

    const {rows, maxColumns} = this.buildTimeSlotGrid(sessionsByResource);
    return rows.map(row => ({
      timeSlot: row.timeSlot,
      timeString: row.timeString,
      sessionsByResourceAndColumn: row.sessionsByKeyAndColumn,
      maxColumns
    }));
  }

  getResourceSessionRows() {
    const sessionsByDay = {...this.resourceSessionsByDay};
    const {rows, maxColumns} = this.buildTimeSlotGrid(sessionsByDay);
    return rows.map(row => ({
      timeSlot: row.timeSlot,
      timeString: row.timeString,
      sessionsByDayAndColumn: row.sessionsByKeyAndColumn,
      maxColumns
    }));
  }

  openScheduleAnalysis() {
    this.openAnalysisDialog(ScheduleAnalysisDialogComponent, '800px', '90vw');
  }

  openTechnicalAnalysis() {
    this.openAnalysisDialog(TechnicalAnalysisDialogComponent, '900px', '95vw');
  }

  private openAnalysisDialog(component: any, width: string, maxWidth: string) {
    this.dialog.open(component, {
      width,
      maxWidth,
      data: {
        scheduleId: this.selectedScheduleId,
        versionNumber: this.selectedScheduleVersionInfo.number || 1,
      }
    });
  }
}
